import { EVENT_DELIVERY, type RealtimeEventType, type ServerEvent, type Seq, type Topic } from '@cyphex/types';

/** Deduplication window across a reconnect overlap (protocol §8). */
const SEEN_FRAMES = 256;

export type Admission = 'apply' | 'drop' | 'gap';

export function isDurable(type: RealtimeEventType): boolean {
  return EVENT_DELIVERY[type] === 'durable';
}

/**
 * Ordering, idempotency and gap detection — protocol §8/§9, kept out of the
 * socket so it can be reasoned about (and unit-tested) on its own.
 *
 * Three rules it enforces: order by `seq` never by time; apply every durable
 * event at most once; hold frames that land after a gap until the replay fills
 * it, so the UI never renders a message that is out of order.
 */
export class Sequencer {
  private readonly applied = new Map<Topic, Seq>();
  private readonly seenIds = new Set<string>();
  private readonly seenOrder: string[] = [];
  private readonly held = new Map<Topic, ServerEvent[]>();
  private readonly catchup = new Set<Topic>();

  /** True when this exact server frame was already processed. */
  isDuplicateFrame(id: string): boolean {
    if (this.seenIds.has(id)) return true;
    this.seenIds.add(id);
    this.seenOrder.push(id);
    if (this.seenOrder.length > SEEN_FRAMES) {
      const evicted = this.seenOrder.shift();
      if (evicted !== undefined) this.seenIds.delete(evicted);
    }
    return false;
  }

  /** Highest applied seq per topic — the `resume`/`sub` cursor payload. */
  cursors(): Record<Topic, Seq> {
    return Object.fromEntries(this.applied);
  }

  cursorsFor(topics: readonly Topic[]): Record<Topic, Seq> {
    const out: Record<Topic, Seq> = {};
    for (const topic of topics) {
      const seq = this.applied.get(topic);
      if (seq !== undefined) out[topic] = seq;
    }
    return out;
  }

  highWater(topic: Topic): Seq {
    return this.applied.get(topic) ?? 0;
  }

  /** Adopt the server's high-water mark for a topic we have no history for. */
  seed(topic: Topic, seq: Seq): void {
    if (!this.applied.has(topic)) this.applied.set(topic, seq);
  }

  commit(topic: Topic, seq: Seq): void {
    if (seq > this.highWater(topic)) this.applied.set(topic, seq);
  }

  /** Forget a topic (or everything) — the caller must cold-load over REST. */
  reset(topic?: Topic): void {
    if (topic === undefined) {
      this.applied.clear();
      this.held.clear();
      this.catchup.clear();
      return;
    }
    this.applied.delete(topic);
    this.held.delete(topic);
    this.catchup.delete(topic);
  }

  beginCatchup(topic: Topic): void {
    this.catchup.add(topic);
  }

  endCatchup(topic: Topic): void {
    this.catchup.delete(topic);
  }

  /**
   * `apply` — deliver now. `drop` — already seen. `gap` — durable event beyond
   * the next expected seq; the caller issues a `resync` and holds the frame.
   */
  admit(frame: ServerEvent): Admission {
    // VOLATILE EVENTS ARE NOT PART OF THE SEQUENCE.
    //
    // `seq` numbers the DURABLE history of a topic. A volatile frame — typing,
    // read receipts, unread counts, drafts, saves, reminders, presence — is
    // stamped with the topic's CURRENT seq and never advances it, so on a quiet
    // conversation every one of them arrives with `seq === highWater` and the
    // dedup rule below threw all of them away. Typing indicators never showed,
    // read ticks never moved, unread badges only corrected on reload.
    //
    // Ordering and gap detection are meaningless for these: they carry no
    // history to replay and nothing downstream depends on their order. Exact
    // re-delivery is still caught upstream by `isDuplicateFrame`, which keys on
    // the frame id rather than the sequence.
    if (!isDurable(frame.event.type)) return 'apply';

    const last = this.highWater(frame.topic);
    if (frame.seq <= last) return 'drop';
    if (this.catchup.has(frame.topic)) return 'apply';
    if (frame.seq > last + 1) return 'gap';
    return 'apply';
  }

  hold(frame: ServerEvent): void {
    const queue = this.held.get(frame.topic) ?? [];
    queue.push(frame);
    this.held.set(frame.topic, queue);
  }

  /** Frames held during a gap, in seq order, minus anything already applied. */
  release(topic: Topic): ServerEvent[] {
    const queue = this.held.get(topic) ?? [];
    this.held.delete(topic);
    const last = this.highWater(topic);
    return queue.filter((frame) => frame.seq > last).sort((a, b) => a.seq - b.seq);
  }
}
